import { useEffect, useState } from "react";
import { Plus, Trash2, Save, X } from "lucide-react";
import { useSite } from "@/lib/site";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ImageUpload from "./ImageUpload";

const EMPTY = { title_en: "", title_es: "", before_url: "", after_url: "", order: 0 };

export default function GalleryEditor({ onChanged }) {
  const { gallery, reload } = useSite();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing && editing !== "new") {
      const g = gallery.find((x) => x.id === editing);
      if (g) setForm({ ...g });
    } else if (editing === "new") {
      setForm({ ...EMPTY, order: (gallery?.length || 0) + 1 });
    }
  }, [editing, gallery]);

  const refresh = async () => { await reload(); onChanged?.(); };

  const onSave = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/gallery", form);
        toast.success("Added to gallery");
      } else {
        await api.put(`/gallery/${editing}`, form);
        toast.success("Updated");
      }
      setEditing(null);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this gallery item?")) return;
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Deleted");
      await refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-black text-2xl tracking-tight">Gallery</h2>
          <div className="tech-label">Before / After repair photos</div>
        </div>
        <Button onClick={() => setEditing("new")} className="bg-brand-red hover:bg-brand-redHover rounded-none" data-testid="gallery-add">
          <Plus size={14} className="mr-2" /> Add new
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {gallery?.map((g, idx) => (
          <div key={g.id} className="bg-white border border-zinc-300" data-testid={`gallery-card-${idx}`}>
            <div className="grid grid-cols-2">
              <div className="aspect-square overflow-hidden bg-zinc-100 border-r border-zinc-300 relative">
                {g.before_url && <img src={g.before_url} alt="before" className="w-full h-full object-cover" />}
                <span className="absolute top-1 left-1 bg-zinc-900 text-white text-[9px] font-black px-1.5 py-0.5 tracking-wider">BEFORE</span>
              </div>
              <div className="aspect-square overflow-hidden bg-zinc-100 relative">
                {g.after_url && <img src={g.after_url} alt="after" className="w-full h-full object-cover" />}
                <span className="absolute top-1 left-1 bg-brand-red text-white text-[9px] font-black px-1.5 py-0.5 tracking-wider">AFTER</span>
              </div>
            </div>
            <div className="p-3 flex items-center justify-between gap-2 border-t border-zinc-300">
              <div className="min-w-0">
                <div className="font-display font-bold text-sm truncate">{g.title_en || "(untitled)"}</div>
                <div className="tech-label !text-[10px]">Order: {g.order}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(g.id)} className="text-xs font-bold text-zinc-700 hover:text-brand-red px-2 py-1" data-testid={`gallery-edit-${idx}`}>Edit</button>
                <button onClick={() => onDelete(g.id)} className="text-xs font-bold text-red-600 hover:text-red-800 px-2 py-1 inline-flex items-center gap-1" data-testid={`gallery-delete-${idx}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-auto">
          <div className="bg-white max-w-2xl w-full border border-zinc-300 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-5 border-b border-zinc-300 sticky top-0 bg-white z-10">
              <h3 className="font-display font-black text-xl tracking-tight">
                {editing === "new" ? "New Gallery Item" : "Edit Gallery Item"}
              </h3>
              <button onClick={() => setEditing(null)} className="text-zinc-500 hover:text-zinc-900"><X /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="tech-label mb-1 block">Title (EN)</label>
                  <Input value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className="rounded-none h-10" />
                </div>
                <div>
                  <label className="tech-label mb-1 block">Title (ES)</label>
                  <Input value={form.title_es} onChange={(e) => setForm({ ...form, title_es: e.target.value })} className="rounded-none h-10" />
                </div>
                <div>
                  <label className="tech-label mb-1 block">Display order</label>
                  <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="rounded-none h-10" />
                </div>
              </div>
              <ImageUpload testId="gallery-before" label="BEFORE image" value={form.before_url} onChange={(v) => setForm({ ...form, before_url: v })} />
              <ImageUpload testId="gallery-after" label="AFTER image" value={form.after_url} onChange={(v) => setForm({ ...form, after_url: v })} />
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-zinc-300 sticky bottom-0 bg-white">
              <Button variant="outline" onClick={() => setEditing(null)} className="rounded-none">Cancel</Button>
              <Button onClick={onSave} disabled={saving} className="bg-brand-red hover:bg-brand-redHover rounded-none" data-testid="gallery-save">
                <Save size={14} className="mr-2" /> {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
